import { Bubbles } from "components/molecules";
import { BackButton, Content, Page, TopBar } from "components/templates";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import { lensHref, routeHref, topicHref } from "utils/learnPaths";
import { LEARN_LENSES } from "utils/learnLenses";
import { learnRoutes } from "utils/learnRoutes";
import {
  LEARN_CATEGORY_LABELS,
  LearnCategory,
  learnTopics,
} from "utils/learnTopics";
import { AvailableLanguages } from "utils/types";

const CATEGORY_ORDER: LearnCategory[] = [
  "foundations",
  "history",
  "development",
  "geopolitics",
  "society",
  "reading-data",
];

const PageTitle = styled.h1`
  margin: 0 0 8px;
  font-family: "Gaegu", cursive;
  font-size: 2.4rem;
  color: #f5e2c8;
  text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35);
`;

const Intro = styled.p`
  margin: 0 0 26px;
  max-width: 100%;
  font-family: "Gaegu", cursive;
  font-size: 1.25rem;
  line-height: 1.4;
  color: rgba(245, 226, 200, 0.92);
`;

const Section = styled.section`
  margin-bottom: 28px;
`;

const SectionLabel = styled.h2`
  margin: 0 0 12px;
  font-family: "Finger Paint", cursive;
  font-size: 1.05rem;
  letter-spacing: 0.3px;
  color: rgba(173, 216, 230, 0.95);
`;

const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 980px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TopicCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(245, 226, 200, 0.16);
  background: rgba(27, 38, 79, 0.22);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  transition:
    transform 140ms ease,
    border-color 140ms ease,
    background 140ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(173, 216, 230, 0.55);
    background: rgba(27, 38, 79, 0.34);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 3px;
  }
`;

const TopicEmoji = styled.span`
  font-size: 1.8rem;
  line-height: 1;
`;

const TopicTitle = styled.h3`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.35rem;
  color: #f5e2c8;
  line-height: 1.2;
`;

const TopicSummary = styled.p`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  line-height: 1.3;
  color: rgba(245, 226, 200, 0.8);
`;

// A route card is the headline format of the section, so it gets a wider,
// more editorial treatment than a library card.
const RouteGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RouteCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(245, 185, 68, 0.38);
  background: linear-gradient(
    140deg,
    rgba(245, 185, 68, 0.14),
    rgba(27, 38, 79, 0.3)
  );
  text-decoration: none;
  transition:
    transform 140ms ease,
    border-color 140ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(245, 185, 68, 0.8);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 3px;
  }
`;

const RouteTitle = styled.h3`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 1.5rem;
  line-height: 1.15;
  color: #f5e2c8;
`;

const RouteSubtitle = styled.p`
  margin: 0;
  font-family: "Finger Paint", cursive;
  font-size: 0.76rem;
  letter-spacing: 0.2px;
  color: rgba(245, 185, 68, 0.95);
`;

const RouteIntro = styled.p`
  margin: 4px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  line-height: 1.3;
  color: rgba(245, 226, 200, 0.82);
`;

const RouteStops = styled.p`
  margin: 8px 0 0;
  font-family: "Gaegu", cursive;
  font-size: 1rem;
  color: rgba(245, 226, 200, 0.62);
`;

const LensRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const LensCard = styled(Link)`
  flex: 1 1 220px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 15px;
  border-radius: 16px;
  border: 1px solid rgba(173, 216, 230, 0.32);
  background: rgba(27, 38, 79, 0.26);
  color: #f5e2c8;
  text-decoration: none;
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.2;
  transition:
    transform 140ms ease,
    border-color 140ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(173, 216, 230, 0.8);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 3px;
  }
`;

const LayerLead = styled.p`
  margin: -4px 0 12px;
  max-width: 720px;
  font-family: "Gaegu", cursive;
  font-size: 1.1rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.72);
`;

const ui = {
  en: {
    title: "Learn",
    intro:
      "Beyond the map: short reads on the history, economics and politics behind the countries you're exploring.",
    back: "Back",
    backAria: "Go back",
    routesLabel: "Routes",
    routesLead:
      "Follow one thread across several countries — each stop links to that country on the map.",
    lensesLabel: "Lenses",
    lensesLead:
      "The same world map, recoloured by one dimension at a time. Patterns you can't see country by country.",
    libraryLabel: "Library",
    libraryLead: "Standalone reads, grouped by theme.",
    stops: (n: number) => `${n} stops`,
  },
  es: {
    title: "Aprender",
    intro:
      "Más allá del mapa: lecturas breves sobre la historia, la economía y la política detrás de los países que explorás.",
    back: "Volver",
    backAria: "Volver",
    routesLabel: "Rutas",
    routesLead:
      "Seguí un mismo hilo a través de varios países — cada parada enlaza a ese país en el mapa.",
    lensesLabel: "Lentes",
    lensesLead:
      "El mismo mapa del mundo, coloreado por una dimensión a la vez. Patrones que país por país no se ven.",
    libraryLabel: "Biblioteca",
    libraryLead: "Lecturas independientes, agrupadas por tema.",
    stops: (n: number) => `${n} paradas`,
  },
};

export const Learn = () => {
  const navigate = useNavigate();
  const { language } = useAppLanguage();
  const isSpanish = language === AvailableLanguages.ES;
  const t = isSpanish ? ui.es : ui.en;

  return (
    <Page id="learn" lang={isSpanish ? "es" : "en"}>
      <Bubbles />
      <Content>
        <TopBar>
          <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
            <span aria-hidden="true">←</span> {t.back}
          </BackButton>
        </TopBar>

        <PageTitle>{t.title}</PageTitle>
        <Intro>{t.intro}</Intro>

        <Section>
          <SectionLabel>{t.routesLabel}</SectionLabel>
          <LayerLead>{t.routesLead}</LayerLead>
          <RouteGrid>
            {learnRoutes.map((route) => {
              const locale = isSpanish ? route.es : route.en;
              return (
                <RouteCard key={route.slug} to={routeHref(isSpanish, route)}>
                  <TopicEmoji aria-hidden="true">{route.emoji}</TopicEmoji>
                  <RouteTitle>{locale.title}</RouteTitle>
                  <RouteSubtitle>{locale.subtitle}</RouteSubtitle>
                  <RouteIntro>{locale.intro}</RouteIntro>
                  <RouteStops>
                    {t.stops(route.stops.length)} ·{" "}
                    {route.stops.map((stop) => stop.emoji).join(" ")}
                  </RouteStops>
                </RouteCard>
              );
            })}
          </RouteGrid>
        </Section>

        <Section>
          <SectionLabel>{t.lensesLabel}</SectionLabel>
          <LayerLead>{t.lensesLead}</LayerLead>
          <LensRow>
            {LEARN_LENSES.map((lens) => (
              <LensCard key={lens.slug} to={lensHref(isSpanish, lens)}>
                <TopicEmoji aria-hidden="true">{lens.emoji}</TopicEmoji>
                {isSpanish ? lens.es.title : lens.en.title}
              </LensCard>
            ))}
          </LensRow>
        </Section>

        <SectionLabel>{t.libraryLabel}</SectionLabel>
        <LayerLead>{t.libraryLead}</LayerLead>

        {CATEGORY_ORDER.map((category) => {
          const topics = learnTopics.filter((t2) => t2.category === category);
          if (topics.length === 0) return null;

          return (
            <Section key={category}>
              <SectionLabel>
                {isSpanish
                  ? LEARN_CATEGORY_LABELS[category].es
                  : LEARN_CATEGORY_LABELS[category].en}
              </SectionLabel>
              <TopicGrid>
                {topics.map((topic) => {
                  const locale = isSpanish ? topic.es : topic.en;
                  return (
                    <TopicCard key={topic.slug} to={topicHref(isSpanish, topic)}>
                      <TopicEmoji aria-hidden="true">{topic.emoji}</TopicEmoji>
                      <TopicTitle>{locale.title}</TopicTitle>
                      <TopicSummary>{locale.summary}</TopicSummary>
                    </TopicCard>
                  );
                })}
              </TopicGrid>
            </Section>
          );
        })}
      </Content>
    </Page>
  );
};
